import notificationsService from "../services/notifications.service";

// The API wraps its payload under `data`.
const readSettings = (resp) => resp?.data?.data || resp?.data || null;

const readMessage = (resp, fallback) =>
  resp?.data?.data?.error ||
  resp?.data?.error ||
  resp?.data?.message ||
  fallback;

/**
 * GET /notifications/settings — no assistant id, these are per-user and cover
 * the whole workspace.
 */
export const getNotificationSettings = () => async () => {
  try {
    const resp = await notificationsService.getSettings();

    if (resp?.status === 200) {
      return { success: true, response: resp, settings: readSettings(resp) };
    }

    return {
      success: false,
      response: resp,
      message: readMessage(resp, "We couldn't load your notification settings."),
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "We couldn't load your notification settings.",
    };
  }
};

/** PATCH /notifications/settings — send only what changed. */
export const updateNotificationSettings = (payload) => async () => {
  try {
    const resp = await notificationsService.updateSettings(payload);

    if (resp?.status === 200 || resp?.status === 204) {
      return { success: true, response: resp, settings: readSettings(resp) };
    }

    return {
      success: false,
      response: resp,
      message: readMessage(resp, "We couldn't save that change."),
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "We couldn't save that change.",
    };
  }
};

/**
 * GET /notifications - one page of the bell feed. `nextCursor` is null once
 * there are no more, which is what the caller pages on.
 */
export const listNotifications = (params) => async () => {
  try {
    const resp = await notificationsService.getFeed(params);

    if (resp?.status === 200) {
      const body = readSettings(resp) || {};
      return {
        success: true,
        response: resp,
        notifications: Array.isArray(body.data) ? body.data : [],
        nextCursor: body.nextCursor || null,
      };
    }

    return {
      success: false,
      response: resp,
      message: readMessage(resp, "We couldn't load your notifications."),
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "We couldn't load your notifications.",
    };
  }
};

/** GET /notifications/unread-count */
export const getUnreadCount = (params) => async () => {
  try {
    const resp = await notificationsService.getUnreadCount(params);

    if (resp?.status === 200) {
      const body = readSettings(resp);
      // The count may come back bare or under a key.
      const count =
        typeof body === "number"
          ? body
          : body?.count ?? body?.unread ?? body?.unreadCount ?? 0;

      return { success: true, response: resp, count: Number(count) || 0 };
    }

    return { success: false, response: resp, count: 0 };
  } catch (error) {
    return { success: false, count: 0, message: error?.message };
  }
};

/** PATCH /notifications/:id/read */
export const markNotificationRead = (id) => async () => {
  try {
    const resp = await notificationsService.markRead(id);
    const ok = resp?.status === 200 || resp?.status === 204;
    return { success: ok, response: resp };
  } catch (error) {
    return { success: false, message: error?.message };
  }
};

/** POST /notifications/read-all */
export const markAllNotificationsRead = () => async () => {
  try {
    const resp = await notificationsService.markAllRead();
    const ok = resp?.status === 200 || resp?.status === 204;
    return { success: ok, response: resp };
  } catch (error) {
    return { success: false, message: error?.message };
  }
};
